import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openInBrowser } from '../../services/office/browser';
import { estimateBand } from '../../services/workbook/estimate';
import type { IndexState } from '../../services/workbook/WorkbookIndexer';
import type { ModelInfo } from '../../types/agent';
import { ModelDetails } from './ModelSwitch';

/** The screen a workbook the server has never seen opens on.
 *
 *  The pane used to start reading the whole file the moment it opened — a minute or more of work on someone
 *  else's model, with a locked composer and no explanation. This asks first: what the reading is for, that
 *  the sheets go to Peerception to be indexed, roughly how long it will take on THIS workbook, and that it
 *  happens once. The button is the consent.
 *
 *  When the organization allows more than one model it also asks which one, before anything is read: the
 *  first index runs on the model picked here (its provider is where the workbook goes), and so does the first
 *  conversation. Only the allowed models are offered.
 *
 *  A modeller who would rather not send their own model first is offered the demo model, on the website's demo page
 *  (opened in their browser, in the pane's language).
 *
 *  A workbook the server already knows never sees this; it opens straight into the chat. */
export const DEMO_URL = 'https://sculpt-agent.ai/demo';

export function WorkbookIntro({ index, models = [], selected = null, onStart }: {
  index: IndexState;
  /** The models offered, each allowed or not; empty while unknown (the server then picks). */
  models?: ModelInfo[];
  /** The model a new conversation would run on: the one preselected. */
  selected?: string | null;
  /** The model picked, or null when there was nothing to pick. */
  onStart: (model: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const demo = `${DEMO_URL}?lang=${i18n?.language === 'fr' ? 'fr' : 'en'}`;
  const allowed = models.filter((m) => m.allowed);
  const [picked, setPicked] = useState<string | null>(null);
  const model = picked ?? selected ?? allowed[0]?.id ?? null;
  const size = index.size;
  const band = size ? estimateBand(size) : null;
  const busy = index.phase === 'probing';
  return (
    <div className="flex flex-1 flex-col justify-center overflow-y-auto px-5 py-6" data-testid="workbook-intro">
      <div className="mx-auto flex w-full max-w-[320px] flex-col gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-ink">{t('intro.title')}</h2>
          <p className="mt-1.5 text-[13px] leading-normal text-muted">{t('intro.why')}</p>
        </div>

        <ul className="flex flex-col gap-2 text-[12.5px] leading-[1.45] text-muted">
          <Point>{t('intro.pointUpload')}</Point>
          <Point>{t('intro.pointOnce')}</Point>
          {band && (
            <Point>
              {band.key === 'under1'
                ? t('intro.timeUnder1')
                : band.key === 'about1'
                  ? t('intro.timeAbout1')
                  : t('intro.timeRange', { low: band.low, high: band.high })}
              {size && size.sheets > 0 && <span className="text-fainter"> · {t('intro.sheets', { count: size.sheets })}</span>}
            </Point>
          )}
        </ul>

        {allowed.length > 1 && (
          <div>
            <h3 className="text-[13px] font-semibold text-ink">{t('intro.modelTitle')}</h3>
            <p className="mt-1 text-[12px] leading-[1.45] text-muted">{t('intro.modelWhy')}</p>
            <div role="radiogroup" aria-label={t('intro.modelTitle')} className="mt-2 flex flex-col gap-1">
              {allowed.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={m.id === model}
                  onClick={() => setPicked(m.id)}
                  className={`flex flex-col items-start gap-0.5 rounded-[6px] border px-2.5 py-2 text-left transition-colors hover:bg-hover ${m.id === model ? 'border-accent bg-hover' : 'border-line-soft'}`}
                >
                  <ModelDetails model={m} checked={m.id === model} />
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => onStart(allowed.length > 1 ? model : null)}
          data-testid="start-index"
          className="w-full rounded-[6px] bg-accent py-2.5 text-center text-[13px] font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? t('intro.looking') : t('intro.start')}
        </button>

        {index.error && <p className="text-[12px] text-danger">{t('index.failed', { error: index.error })}</p>}

        <p className="text-center text-[12px] leading-[1.45] text-muted" data-testid="intro-demo">
          {t('intro.demoLead')}{' '}
          <a href={demo} target="_blank" rel="noopener noreferrer" data-testid="intro-demo-link"
            onClick={(e) => { e.preventDefault(); openInBrowser(demo); }}
            className="text-accent underline-offset-2 hover:underline">
            {t('intro.demoLink')}
          </a>
        </p>
      </div>
    </div>
  );
}

function Point({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span aria-hidden="true" className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-line" />
      <span className="min-w-0">{children}</span>
    </li>
  );
}
